import { useEffect, useRef } from 'react';
import { getAnalytics, logEvent, setUserProperties } from 'firebase/analytics';
import { app } from '../config/firebase';

let analyticsSingleton = null;
let userPropsApplied = false;

function getWebAnalytics() {
  if (typeof window === 'undefined') return null;
  if (!analyticsSingleton) {
    try {
      analyticsSingleton = getAnalytics(app);
      if (!userPropsApplied) {
        setUserProperties(analyticsSingleton, { app_platform: 'web' });
        userPropsApplied = true;
      }
    } catch (e) {
      console.warn('[webAnalytics] getAnalytics failed', e);
      return null;
    }
  }
  return analyticsSingleton;
}

/** Matches Android TV BigQuery expectations; always tag Web traffic for /api/analytics/web */
const withPlatform = (params) => ({ ...params, app_platform: 'web' });

function timeParams() {
  const d = new Date();
  return { hour_of_day: d.getHours(), day_of_week: d.getDay() + 1 };
}

/** Once per browser tab session */
export function logWebAppOpenOnce() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (sessionStorage.getItem('ga_web_app_open')) return;
    sessionStorage.setItem('ga_web_app_open', '1');
    const a = getWebAnalytics();
    if (!a) return;
    logEvent(a, 'app_open', withPlatform({
      source: 'GSRadioWeb',
      build_product: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      build_device: typeof navigator !== 'undefined' ? navigator.language : '',
    }));
  } catch (e) {
    console.warn('[webAnalytics] app_open', e);
  }
}

export function logMinisiteOpen(minisiteUrl) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'minisite_open', withPlatform({ minisite_url: minisiteUrl || '' }));
}

export function logMinisiteClose(minisiteUrl, durationSec) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'minisite_close', withPlatform({
    minisite_url: minisiteUrl || '',
    duration_sec: Math.max(0, Math.round(durationSec || 0)),
  }));
}

/** Call from minisite routes (games): open on mount, close on unmount with duration */
export function useMinisiteSessionAnalytics() {
  const openedAtRef = useRef(null);
  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    logMinisiteOpen(url);
    openedAtRef.current = Date.now();
    return () => {
      const sec = openedAtRef.current ? (Date.now() - openedAtRef.current) / 1000 : 0;
      logMinisiteClose(url, sec);
    };
  }, []);
}

// —— Playback / ads (call from web player or ad UI when integrated) ——

export function logSelectContent({ item_id, item_name, content_type }) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'select_content', withPlatform({ item_id, item_name, content_type }));
}

export function logStationPlay({ station_id, station_name, category }) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'station_play', withPlatform({
    station_id,
    station_name,
    category,
    ...timeParams(),
  }));
}

export function logStationStop({
  station_id,
  station_name,
  category,
  listen_duration_sec,
}) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'station_stop', withPlatform({
    station_id,
    station_name,
    category,
    listen_duration_sec,
    ...timeParams(),
  }));
}

export function logPodcastPlay({
  episode_id,
  episode_name,
  podcast_name,
  resume_from_sec,
}) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'podcast_play', withPlatform({
    episode_id,
    episode_name,
    podcast_name,
    resume_from_sec,
    ...timeParams(),
  }));
}

export function logPodcastMilestone({
  episode_id,
  episode_name,
  podcast_name,
  milestone_percent,
}) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'podcast_milestone', withPlatform({
    episode_id,
    episode_name,
    podcast_name,
    milestone_percent,
  }));
}

export function logPodcastCompleted({
  episode_id,
  episode_name,
  podcast_name,
  duration_sec,
}) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'podcast_completed', withPlatform({
    episode_id,
    episode_name,
    podcast_name,
    duration_sec,
  }));
}

export function logBannerImpression({
  ad_id,
  item_id,
  item_name,
  content_type = 'banner',
  station_name,
  station_id,
}) {
  const a = getWebAnalytics();
  if (!a) return;
  const p = withPlatform({
    ad_id,
    item_id,
    item_name,
    content_type,
  });
  if (station_name != null) p.station_name = station_name;
  if (station_id != null) p.station_id = station_id;
  logEvent(a, 'banner_impression', p);
}

export function logQrShown({ ad_id, ad_name }) {
  const a = getWebAnalytics();
  if (!a) return;
  logEvent(a, 'qr_shown', withPlatform({
    ad_id,
    ad_name,
    ...timeParams(),
  }));
}
